import React, { useEffect, useState, useRef, useMemo } from "react";
import SearchLayout from "../../layouts/SearchLayout";
import SearchBar from "../../components/SearchBar/SearchBar";
import { useDispatch } from "react-redux";
import Logo from "../../components/Logo/Logo";
import { homeActions } from "../../store/store";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { InstantSearch } from "react-instantsearch";
import { searchClient } from "../../services/typesense-client";
import { safeRouting } from "../../services/instantsearch-routing";
import { useAuthContext } from "@asgardeo/auth-react";

const Home = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  window.dataLayer = window.dataLayer || [];
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);
  const { state, getAccessToken } = useAuthContext();
  const [userToken, setUserToken] = useState("noToken");
  const searchType = sessionStorage.getItem("searchType");

  useEffect(() => {
    dispatch(homeActions.setCurrentPage("home"));
    dispatch(homeActions.setSelectedOption("All"));
    sessionStorage.setItem("searchType", "All");
    sessionStorage.setItem("isEmpty", true);
    sessionStorage.removeItem("searchedQuery");
    sessionStorage.removeItem("isRedirected");

    return () => {
      dispatch(homeActions.setCurrentPage(""));
    }
  }, [dispatch]);

  const handleClick = () => {
    sessionStorage.removeItem("redirectAfterLogin");
    sessionStorage.removeItem("redirectToDocument");
    sessionStorage.removeItem("urlString");
    navigate("/search");
    window.dataLayer.push({
      event: "Explore Click",
      event_label: "Explore All button clicked",
    });
  };

  let queryBy;
  let inFix;
  let sortBy = "date:desc";

  useEffect(() => {
    const fetchToken = async () => {
      if (state.isAuthenticated) {
        const accessToken = await getAccessToken();
        setUserToken(accessToken);
        sessionStorage.setItem("userToken", accessToken);
      } else {
        setUserToken("noToken");
      }
    };
    fetchToken();
  }, [state.isAuthenticated, getAccessToken]);

  if (searchType === "Exact Match") {
    queryBy = "decision_text";
    inFix = "off";
  } else if (searchType === "Semantic Match") {
    queryBy = "summary_vec";
    inFix = "off";
  } else if (searchType === "Case Number") {
    queryBy = "standard_casenumber";
    inFix = "always";
  } else if (searchType === "Parties") {
    queryBy = "nameofparties";
    inFix = "off";
  } else {
    queryBy = "decision_text,summary_vec";
    inFix = "off";
  }

  // Keep a stable reference for searchClient to avoid extra searches
  const SearchClient = useMemo(
    () => searchClient(queryBy, inFix, userToken, undefined, sortBy),
    [queryBy, inFix, sortBy]
  );
  const PlaceHolder = isDesktop
    ? "Start typing a legal query, key word, party name, or case number"
    : "Type a legal query or key word";

  return (
    <InstantSearch
      indexName={import.meta.env.VITE_TYPESENSE_COLLECTION_NAME}
      searchClient={SearchClient}
      routing={safeRouting}
      future={{
        preserveSharedStateOnUnmount: true,
      }}
    >
      <SearchLayout showSearch={false} showLogo={false} showTab={false}>
        <Helmet>
          <title>paralegal.lk | a search engine for Sri Lankan law</title>
          <meta
            name="description"
            content="Our case law database covers Supreme Court, Court of Appeal and High Court of Civil Appeal plus SLR and NLR law reports."
          />
          <link rel="canonical" href="https://www.paralegal.lk" />
        </Helmet>
        <div className="flex flex-col items-center justify-center px-20 w-full">
          <div className="flex justify-center">
            <Logo />
          </div>

          <div className="flex w-full justify-center my-10 z-10">
            <SearchBar
              PlaceHolder={PlaceHolder}
              enableSuggestions={true}
              enableFiltering={true}
              enableSearchTracking={true}
              SearchAsYouType={true}
            />
          </div>

        {/* Lazy Loaded Text Animation */}
        <div className="z-0 max-w-[800px] w-full justify-between flex animate-pulse text-pl-text-primary dark:font-extralight text-pl-accent uppercase mt-20 m-30 sm:mt-50 sm:mb-60 text-12 text-center px-20 md:px-30">
          {'FAST · CURRENT · RELEVANT'.split('').map((char, index) => (
            <span key={index}>{char}</span>
          ))}
       </div>
        {/* <Suspense fallback={<p className="text-center text-gray-500 animate-pulse">Loading...</p>}>
          <LazyLoadText />
        </Suspense> */}

          <button
            onClick={handleClick}
            className="z-0 max-w-[200px] w-full h-[40px] rounded-pl bg-transparent text-pl-accent border border-pl-accent font-semibold text-14 hover:text-white hover:bg-pl-accent transition-colors duration-150"
          >
            Explore all
          </button>
        </div>
      </SearchLayout>
    </InstantSearch>
  );
};

export default Home;
